import { useCallback, useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import debounce from 'lodash.debounce';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LoadingSpinner, ModalPopup, Stack } from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import { LastSaved } from 'modules/notes/notePopup/noteView/addendumForm/lastSaved';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { getClient, postUpdateSnapshot } from 'store/clients/clientsActions';
import { selectClientSnapshot, setSnapshotDraft } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getOtherScreenerDetails,
  getOtherScreenerHistory,
  getScreenerDetails,
  getScreenerHistory,
  getScreenerQuestions,
  getScreeners
} from 'store/screeener/screenerActions';
import { ScreenerTypeLowercase } from 'store/screeener/screenerSlice';
import { ScreenerModal } from 'pages/clients/clientForm/profileInfo/screener/screener';

import { NoteFormValues } from '../utils/validation';
import style from './MpiNoteActions.module.scss';

const MPI_NOTE_TYPES = [
  'MEET_THE_PERSON_INTERVIEW',
  'MEET_THE_PERSON_INTERVIEW_CHILD',
  'MEET_THE_PERSON_INTERVIEW_TEEN'
];

export const isMpiNoteType = (noteType: string | null | undefined) => {
  return noteType != null && MPI_NOTE_TYPES.includes(noteType);
};

type SnapshotModalProps = {
  clientId: string;
  container?: HTMLElement;
  open: boolean;
  onClose: () => void;
};

function SnapshotModal({ clientId, container, open, onClose }: SnapshotModalProps) {
  const dispatch = useAppDispatch();
  const snapshot = useAppSelector((state) => selectClientSnapshot(state.clients, clientId));
  const { organizationId } = getLsUserRoleObject();
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isAutosaving, setIsAutosaving] = useState(false);

  const autosaveForm = useCallback(
    async (values: { snapshot: string }) => {
      if (!clientId || !organizationId) return;

      setIsAutosaving(true);
      const response = await dispatch(
        postUpdateSnapshot({
          snapshot: values.snapshot,
          clientId,
          organizationId
        })
      );

      setIsAutosaving(false);
      if (response.meta.requestStatus === 'fulfilled') {
        setLastSaved(new Date().toISOString());
      }
    },
    [clientId, dispatch, organizationId]
  );

  const debounceFn = useMemo(() => debounce(autosaveForm, 250), [autosaveForm]);

  useEffect(() => {
    return () => {
      debounceFn.cancel();
    };
  }, [debounceFn]);

  const handleSnapshotChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const clippedSnapshot = event.target.value.slice(0, 500);
    dispatch(setSnapshotDraft({ clientId, snapshot: clippedSnapshot }));

    const channel = new BroadcastChannel('note-update');
    channel.postMessage({
      message: 'SNAPSHOT_UPDATED',
      clientId,
      snapshot: clippedSnapshot
    });
    channel.close();

    debounceFn({ snapshot: clippedSnapshot });
  };

  return (
    <ModalPopup
      open={open}
      container={container}
      customClassInsideContainer={style.snapshotModal}
      customClassModalContentContainer={style.snapshotModalContent}
    >
      <div className={style.snapshotContent}>
        <div className={style.snapshotHeader}>
          <h3>Snapshot</h3>
          <button type="button" className={style.snapshotCloseButton} onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <form onSubmit={(event) => event.preventDefault()}>
          <Stack width="100%" height="128px">
            <TextAreaInputField
              onChange={handleSnapshotChange}
              name="snapshot"
              value={snapshot}
              enableCounter
              maxLength={500}
              rowsNumber={4}
              placeholder="Type here"
              classnamesProps={style.snapshotTextArea}
              autoFocus
            />
          </Stack>
          <div className={style.snapshotAutosave}>
            {isAutosaving ? <LoadingSpinner size={20} /> : <LastSaved lastSaved={lastSaved} />}
          </div>
        </form>
      </div>
    </ModalPopup>
  );
}

type MpiNoteActionsProps = {
  clientId: string;
  visible?: boolean;
};

type OpenModal = 'screener' | 'snapshot' | null;

export const MpiNoteActions = ({ clientId, visible = true }: MpiNoteActionsProps) => {
  const { enableMpiUpdate2 } = useFlags();
  const dispatch = useAppDispatch();
  const formik = useFormikContext<NoteFormValues>();
  const openPreloadClientId = useRef<string | null>(null);
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [modalContainer, setModalContainer] = useState<HTMLElement>();
  const [selectedScreener, setSelectedScreener] = useState<ScreenerTypeLowercase>('impact');
  const [isPreloading, setIsPreloading] = useState(false);
  const client = useAppSelector((state) => state.clients.client.data);
  const screeners = useAppSelector((state) => state.screener[clientId]?.screeners?.data);
  const shouldShowActions =
    Boolean(enableMpiUpdate2) && visible && isMpiNoteType(formik.values.noteType);

  useEffect(() => {
    if (!shouldShowActions || !clientId || openPreloadClientId.current === clientId) return;

    let isMounted = true;
    openPreloadClientId.current = clientId;
    setIsPreloading(true);
    const { organizationId } = getLsUserRoleObject();
    const preloadRequests = [
      dispatch(getScreenerDetails({ clientId, type: 'impact' })),
      dispatch(getScreenerHistory({ clientId, type: 'impact' })),
      dispatch(getScreeners({ clientId })),
      dispatch(getScreenerQuestions({ clientId, type: 'impact' })),
      dispatch(getOtherScreenerDetails({ clientId })),
      dispatch(getOtherScreenerHistory({ clientId }))
    ];

    if (organizationId && client?.id?.toString() !== clientId) {
      preloadRequests.push(dispatch(getClient({ organizationId, clientId, skipLoading: true })));
    }

    Promise.all(preloadRequests).finally(() => {
      if (isMounted) {
        setIsPreloading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [client?.id, clientId, dispatch, shouldShowActions]);

  const getInitialScreener = () => {
    const hasImpactScreener = screeners?.some((screener) => screener.type === 'IMPACT');
    const hasOtherScreener = screeners?.some((screener) => screener.type === 'OTHER');

    return hasImpactScreener && !hasOtherScreener ? 'other' : 'impact';
  };

  if (!shouldShowActions) {
    return null;
  }

  return (
    <>
      <div className={style.mpiActions} data-mpi-actions>
        <button
          className={style.mpiActionButton}
          type="button"
          disabled={isPreloading}
          onClick={(event) => {
            setModalContainer(
              event.currentTarget.closest<HTMLElement>('[data-vaul-drawer]') ?? undefined
            );
            setSelectedScreener(getInitialScreener());
            setOpenModal('screener');
          }}
        >
          Screener
        </button>
        <button
          className={style.mpiActionButton}
          type="button"
          disabled={isPreloading}
          onClick={(event) => {
            setModalContainer(
              event.currentTarget.closest<HTMLElement>('[data-vaul-drawer]') ?? undefined
            );
            setOpenModal('snapshot');
          }}
        >
          Snapshot
        </button>
      </div>
      <ScreenerModal
        clientId={clientId}
        container={modalContainer}
        open={openModal === 'screener'}
        selectedScreener={selectedScreener}
        onSelectScreener={setSelectedScreener}
        onClose={() => setOpenModal(null)}
      />
      <SnapshotModal
        clientId={clientId}
        container={modalContainer}
        open={openModal === 'snapshot'}
        onClose={() => setOpenModal(null)}
      />
    </>
  );
};
